import React, { createContext, useContext, useState, useEffect } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';

// Create the context
const CategoriesContext = createContext();

// Custom hook to use the context
export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
};

// Provider component
export const CategoriesProvider = ({ children }) => {
  // Initialize categories from localStorage or use default values
  const [categories, setCategories] = useState(() => {
    const savedCategories = localStorage.getItem('facit_categories_data');
    return savedCategories ? JSON.parse(savedCategories) : [
      { id: 1, name: 'Services Informatiques', description: 'Développement, maintenance, consulting IT', color: '#3b82f6', productCount: 2, prefix: 'SRV' },
      { id: 2, name: 'Matériel Informatique', description: 'Ordinateurs, serveurs, périphériques', color: '#10b981', productCount: 2, prefix: 'MAT' },
      { id: 3, name: 'Formation', description: 'Cours et formations professionnelles', color: '#f59e0b', productCount: 1, prefix: 'FOR' },
      { id: 4, name: 'Consulting', description: 'Conseil et audit', color: '#8b5cf6', productCount: 1, prefix: 'CST' },
      { id: 5, name: 'Maintenance', description: 'Services de maintenance et support', color: '#ef4444', productCount: 0, prefix: 'MNT' }
    ];
  });

  // Save categories to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('facit_categories_data', JSON.stringify(categories));
  }, [categories]);

  // Add a new category
  const addCategory = (category) => {
    const newCategory = {
      id: Math.max(0, ...categories.map(c => c.id)) + 1,
      ...category,
      productCount: 0
    };
    
    const updatedCategories = [...categories, newCategory];
    setCategories(updatedCategories);
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.CATEGORY_CREATED, newCategory);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'categories');
    
    return newCategory;
  };

  // Update an existing category
  const updateCategory = (id, updatedCategory) => {
    const oldCategory = categories.find(category => category.id === id);
    const newCategories = categories.map(category => 
      category.id === id ? { ...category, ...updatedCategory } : category
    );
    
    setCategories(newCategories);
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.CATEGORY_UPDATED, {
      oldCategory,
      newCategory: newCategories.find(category => category.id === id)
    });
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'categories');
  };

  // Delete a category
  const deleteCategory = (id) => {
    const categoryToDelete = categories.find(category => category.id === id);
    const newCategories = categories.filter(category => category.id !== id);
    setCategories(newCategories);
    
    // Emit events
    eventEmitter.emit(EVENT_TYPES.CATEGORY_DELETED, categoryToDelete);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED, 'categories');
  };

  // Increment product count for a category
  const incrementProductCount = (categoryName) => {
    const categoryIndex = categories.findIndex(cat => cat.name === categoryName);
    if (categoryIndex !== -1) {
      const updatedCategories = [...categories];
      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        productCount: (updatedCategories[categoryIndex].productCount || 0) + 1
      };
      setCategories(updatedCategories);
    }
  };

  // Decrement product count for a category
  const decrementProductCount = (categoryName) => {
    const categoryIndex = categories.findIndex(cat => cat.name === categoryName);
    if (categoryIndex !== -1 && categories[categoryIndex].productCount > 0) {
      const updatedCategories = [...categories];
      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        productCount: updatedCategories[categoryIndex].productCount - 1
      };
      setCategories(updatedCategories);
    }
  };

  // Get category by name
  const getCategoryByName = (name) => {
    return categories.find(cat => cat.name === name);
  };

  // Get category by prefix
  const getCategoryByPrefix = (prefix) => {
    return categories.find(cat => cat.prefix === prefix);
  };

  return (
    <CategoriesContext.Provider
      value={{
        categories,
        addCategory,
        updateCategory,
        deleteCategory,
        getCategoryByName,
        getCategoryByPrefix,
        incrementProductCount,
        decrementProductCount
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
};

export default CategoriesContext; 